import { Phone, Mail, Check, Keyboard, Eye, Type, MousePointer2 } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { COMPANY } from "@/lib/constants";

const FEATURES = [
  { icon: Keyboard, title: "Keyboard Navigation", desc: "All interactive elements are reachable and operable with a keyboard." },
  { icon: Eye, title: "High Contrast & Color", desc: "Text and controls meet WCAG AA contrast ratios for readability." },
  { icon: Type, title: "Readable Typography", desc: "Large, legible fonts that scale cleanly when you zoom or resize text." },
  { icon: MousePointer2, title: "Clear Focus States", desc: "Visible focus indicators help everyone track where they are on the page." },
];

export default function Accessibility() {
  useSEO({
    title: "Accessibility Statement | Esprit Home Healthcare",
    description: "Esprit Home Healthcare is committed to making our website accessible to everyone, in line with WCAG 2.1 AA standards.",
    canonical: "/accessibility",
  });

  return (
    <div>
      <PageHeader
        overline="Accessibility"
        title="Accessible to Everyone"
        subtitle="We are committed to ensuring our website is usable by all visitors, including people with disabilities, in line with recognized accessibility standards."
      />

      <section className="container-page py-16 lg:py-20 max-w-3xl">
        <Reveal>
          <h2 className="font-heading font-bold text-3xl text-primary">Our Commitment</h2>
          <p className="text-lg text-foreground/80 mt-4 leading-relaxed">
            Esprit Home Healthcare strives to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA. These guidelines explain how to make web content more accessible for people with a wide range of abilities, and improve the experience for everyone.
          </p>
        </Reveal>

        <Reveal>
          <div className="grid sm:grid-cols-2 gap-4 mt-10">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-card rounded-2xl border border-border p-6">
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary/10 text-primary"><f.icon className="w-6 h-6" /></span>
                <h3 className="font-heading font-bold text-xl text-primary mt-4">{f.title}</h3>
                <p className="text-muted-foreground mt-1">{f.desc}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal>
          <h2 className="font-heading font-bold text-3xl text-primary mt-12">Ongoing Efforts</h2>
          <ul className="space-y-3 mt-4">
            {["Regular reviews of new and existing content", "Descriptive alternative text for meaningful images", "Semantic, well-structured headings and landmarks", "Forms with clear, associated labels", "Respect for reduced-motion preferences"].map((t) => (
              <li key={t} className="flex items-start gap-3 text-lg text-foreground/90">
                <Check className="w-6 h-6 text-secondary shrink-0 mt-0.5" /> {t}
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal>
          <div className="bg-[hsl(var(--muted))] rounded-[2rem] p-8 mt-12">
            <h2 className="font-heading font-bold text-2xl text-primary">Need help or found a barrier?</h2>
            <p className="text-muted-foreground mt-2">
              If you experience any difficulty accessing part of our website, or have suggestions to improve accessibility, please contact us — we're happy to help and to provide information in an alternative format.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-5">
              <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-14 px-7 text-lg font-bold transition-colors">
                <Phone className="w-5 h-5" /> {COMPANY.phone}
              </a>
              <a href={`mailto:${COMPANY.email}`} className="inline-flex items-center gap-2 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground h-14 px-7 text-lg font-bold transition-colors">
                <Mail className="w-5 h-5" /> {COMPANY.email}
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
